import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { FileText, CalendarDays } from 'lucide-react'
import { ValidityPeriodPicker } from './ValidityPeriodPicker'

interface TemplateInfo {
  id: string
  name: string | null
  status: string
  version: number
}

interface BulkImportDialogProps {
  isOpen: boolean
  onClose: () => void
  fileName: string
  pageCount: number
  templates: TemplateInfo[]
  recommendedTemplateId: string | null
  onStart: (templateId: string | null, validFrom?: string | null, validTo?: string | null) => void
}

export function BulkImportDialog({
  isOpen,
  onClose,
  fileName,
  pageCount,
  templates,
  recommendedTemplateId,
  onStart,
}: BulkImportDialogProps) {
  const { t } = useTranslation()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const initializedRef = useRef(false)

  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true
      if (recommendedTemplateId && templates.some(t => t.id === recommendedTemplateId)) {
        setSelectedTemplateId(recommendedTemplateId)
      } else if (templates.length === 1) {
        setSelectedTemplateId(templates[0]?.id ?? null)
      } else {
        setSelectedTemplateId(null)
      }
      setValidFrom('')
      setValidTo('')
    }
    if (!isOpen) {
      initializedRef.current = false
    }
  }, [isOpen, recommendedTemplateId, templates])

  const handleConfirm = () => {
    onStart(selectedTemplateId, validFrom || null, validTo || null)
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bulkImport_title') as string}
      size="lg"
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <div>
              <div className="font-medium text-blue-800">
                {(t('bulkImport_fileInfo') as string)
                  .replace('{{filename}}', fileName)
                  .replace('{{pageCount}}', String(pageCount))}
              </div>
              <div className="text-sm text-blue-600">
                Jede Seite wird als eigener Stundenplan verarbeitet
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('bulkImport_templateSelection')}
          </label>
          {templates.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
              {t('bulkImport_noTemplates')}
            </div>
          ) : (
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={selectedTemplateId || ''}
              onChange={(e) => setSelectedTemplateId(e.target.value || null)}
            >
              <option value="">Keine Vorlage</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name || `Unbenannt`} (v{tpl.version}, {tpl.status})
                  {tpl.id === recommendedTemplateId ? ' [empfohlen]' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="h-4 w-4 text-gray-600" />
            <label className="block text-sm font-medium text-gray-700">
              Gültigkeitsbereich
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Gilt für alle importierten Stundenpläne dieses Bulk-Imports
          </p>
          <ValidityPeriodPicker
            validFrom={validFrom}
            validTo={validTo}
            onChange={(from, to) => {
              setValidFrom(from)
              setValidTo(to)
            }}
          />
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" className="flex-1" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={handleConfirm}
          >
            {t('bulkImport_startImport')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}